import {
  Button,
  Kbd,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import { PiArrowArcLeft, PiCheck, PiTrash, PiWarning } from 'react-icons/pi';
import { XIcon } from 'lucide-react';
import React, { useCallback, useEffect, useRef } from 'react';
import { cn, isNonEmpty } from '@ais-ui/utils';
import {
  featureToShape,
  GeometryTextEditorPopover,
  shapeToFeature,
  useDrawingContext,
  useOpenLayersContext,
} from '@ais-ui/react-openlayers';
import { GeoJson } from '@ais-ui/api-spec/shapes';

interface DrawingCardProps {
  children?: React.ReactNode;
  className?: string;
}

/**
 * DrawingMenu - toolbar displayed during shape drawing on the map.
 *
 * Uses DrawingContext for all state and actions:
 * - onAccept: finish and accept the drawing
 * - onUndo: remove last point
 * - onClear: clear the entire drawing
 * - onCancel: cancel and exit drawing mode
 * - isValidDrawing: validation state for enabling/disabling finish button
 * - drawnFeature: ref to current feature (used to enable/disable undo/clear)
 */
export const DrawingMenu = ({ children, className }: DrawingCardProps) => {
  const {
    onFinish,
    onAccept,
    onCancel,
    onClear,
    onUndo,
    isValidDrawing,
    radius,
    geometryType,
    drawnFeature,
    setRadius,
    replaceFeature,
  } = useDrawingContext();
  const { displayProjection, dataProjection } = useOpenLayersContext();

  const acceptButtonRef = useRef<HTMLButtonElement>(null);

  // Keyboard shortcuts: Enter to finish, Escape to cancel
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && acceptButtonRef.current) {
        acceptButtonRef.current.click();
      }
      if (event.key === 'Escape' && onFinish.current) {
        onFinish.current();
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [onFinish]);

  // Convert current feature to shape for text editor
  const currentShape = drawnFeature.current
    ? featureToShape(drawnFeature.current, displayProjection, dataProjection)
    : null;

  // Handle geometry change from text editor
  const handleGeometryApply = useCallback(
    (newShape: GeoJson) => {
      const feature = shapeToFeature(
        newShape,
        displayProjection,
        dataProjection,
      );
      if (feature && replaceFeature.current) {
        drawnFeature.current = feature;

        // Update radius if it's a circle
        if (newShape.properties?.radius) {
          setRadius(newShape.properties.radius);
        }

        // Replace the feature on the map
        replaceFeature.current(feature);
      }
    },
    [
      drawnFeature,
      setRadius,
      replaceFeature,
      displayProjection,
      dataProjection,
    ],
  );

  // Don't render if drawing callbacks aren't set up
  if (
    !onFinish.current ||
    !onAccept.current ||
    !onCancel.current ||
    !onClear.current ||
    !onUndo.current
  ) {
    return null;
  }

  const hasWarning = isValidDrawing.isValid && isValidDrawing.warning;
  const hasDrawnSomething = drawnFeature.current !== null;

  return (
    <div
      className={cn(
        'bg-card border border-border rounded-xl shadow-md px-3 py-3 w-fit',
        className,
      )}
    >
      {geometryType === 'Circle' && isNonEmpty(radius) && (
        <div className="flex space-x-2 pb-2 mb-3 ml-2 mt-1 border-b border-border">
          <p className="text-base font-medium flex-none">Radius</p>
          <p className="text-base text-muted-foreground flex-grow truncate">
            {radius / 1000} kilometers
          </p>
        </div>
      )}
      <div className="flex gap-3 items-center">
        {children}

        {/* Finish button */}
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-block">
              <Button
                ref={acceptButtonRef}
                id="drawingcard-finish-button"
                disabled={!isValidDrawing.isValid}
                size="lg"
                className={cn(
                  'px-6 py-3 flex items-center gap-2 text-white text-base',
                  !isValidDrawing.isValid
                    ? 'bg-gray-400 cursor-not-allowed'
                    : hasWarning
                      ? 'bg-amber-500 hover:bg-amber-600'
                      : 'bg-green-600 hover:bg-green-700',
                )}
                onClick={onAccept.current}
              >
                {hasWarning ? (
                  <PiWarning className="size-5" />
                ) : (
                  <PiCheck className="size-5" />
                )}
                Finish
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            {!isValidDrawing.isValid ? (
              isValidDrawing.reason
            ) : hasWarning ? (
              <div className="space-y-1">
                <div className="flex items-start gap-1.5">
                  <PiWarning className="size-4 text-amber-500 flex-shrink-0 mt-0.5" />
                  <ul className="space-y-0.5">
                    {isValidDrawing.warning
                      ?.split('. ')
                      .filter(Boolean)
                      .map((warning, index) => (
                        <li key={index} className="text-sm">
                          {warning.endsWith('.') ? warning : `${warning}.`}
                        </li>
                      ))}
                  </ul>
                </div>
                <div className="flex items-center gap-1 text-muted-foreground">
                  Press <Kbd className="text-xs mx-1 p-0">Enter</Kbd> to finish
                  anyway
                </div>
              </div>
            ) : (
              <span className="flex items-center gap-1">
                Finish, use <Kbd className="text-xs mx-1 p-0">Enter</Kbd>
              </span>
            )}
          </TooltipContent>
        </Tooltip>

        {/* Undo button */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              id="drawingcard-undo-button"
              variant="ghost"
              size="lg"
              onClick={onUndo.current}
              disabled={!hasDrawnSomething}
              className="p-3 hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <PiArrowArcLeft className="size-5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Undo</TooltipContent>
        </Tooltip>

        {/* Clear button */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              id="drawingcard-clear-button"
              onClick={onClear.current}
              variant="ghost"
              size="lg"
              disabled={!hasDrawnSomething}
              className="p-3 hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <PiTrash className="size-5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Clear</TooltipContent>
        </Tooltip>

        {/* Text geometry editor - uses shared component */}
        {geometryType && (
          <GeometryTextEditorPopover
            geometryType={geometryType}
            shape={currentShape ?? null}
            onApply={handleGeometryApply}
            buttonSize="lg"
            buttonVariant="ghost"
            buttonClassName="p-3 hover:bg-muted"
            popoverSide="top"
          />
        )}

        {/* Cancel button */}
        <Button
          id="drawingcard-cancel-button"
          onClick={onCancel.current}
          variant="ghost"
          size="lg"
          className="p-3 hover:bg-muted"
        >
          <XIcon className="size-5" />
        </Button>
      </div>
    </div>
  );
};
